/**
 * Theme store
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { THEMES, STORAGE_KEYS } from '@/utils/constants'

export const useThemeStore = defineStore('theme', () => {
  // State
  const currentTheme = ref<string>(THEMES.LIGHT)

  // Getters
  const isDark = computed(() => currentTheme.value === THEMES.DARK)
  const isLight = computed(() => currentTheme.value === THEMES.LIGHT)

  // Actions
  function setTheme(theme: string) {
    if (theme !== THEMES.LIGHT && theme !== THEMES.DARK) {
      console.warn(`Invalid theme: ${theme}. Using light theme.`)
      theme = THEMES.LIGHT
    }

    currentTheme.value = theme
    
    // Update HTML class
    if (theme === THEMES.DARK) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }

    const themeColor = theme === THEMES.DARK ? '#111827' : '#0d9488'
    document.querySelectorAll('meta[name="theme-color"]').forEach((meta) => {
      if (!meta.hasAttribute('media')) {
        meta.setAttribute('content', themeColor)
      }
    })
    
    // Save to localStorage
    localStorage.setItem(STORAGE_KEYS.THEME, theme)
  }

  function toggleTheme() {
    const newTheme = currentTheme.value === THEMES.LIGHT ? THEMES.DARK : THEMES.LIGHT
    setTheme(newTheme)
  }

  function initializeTheme() {
    // Load from localStorage or system preference
    const storedTheme = localStorage.getItem(STORAGE_KEYS.THEME)
    
    if (storedTheme) {
      setTheme(storedTheme)
    } else {
      // Check system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
      setTheme(prefersDark ? THEMES.DARK : THEMES.LIGHT)
    }
  }

  return {
    // State
    currentTheme,
    // Getters
    isDark,
    isLight,
    // Actions
    setTheme,
    toggleTheme,
    initializeTheme
  }
})
